import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import dayjs from "dayjs";
import moment from "moment";
import cx from "classnames";
import { isEmpty } from "ramda";
import Calendar from "./Calendar/Calendar";
import {
  fetchBookNowDetail,
  getBookNowDataWithDateObject,
} from "../../dux/bookNow";
import { mapStartTimeToIndex, mapTurf } from "../../helpers/bookNowHelper";
import styles from "./BlockSlot.module.css";

import {
  // apiCallComplete,
  isBlockUnblockPending,
  isFetchBookNowPending,
} from "../../dux/apiStatus";
import Loader from "../shared/UI/Loader/Loader";
import LoaderForButton from "../shared/UI/LoaderForButton";
import { blockUnblockSlot } from "../../dux/blockSlot";
import { getUser } from "../../helpers/sessionStorageHelper";
import { getCurrentTimeString } from "../../helpers/dateHelper";
import { setBookNowDetail } from "../../dux/bookNow";
import { parseBookNowApiData } from "../../helpers/bookNowHelper";

// 🆕 Location-wise sport availability
const locationSportsMap = {
  srinagar: ["Football", "Box Cricket", "Cricket Net", "Pickle Ball"],
  // baramulla: ["Pickle Ball"],
  jammu: ["Pickle Ball"],
};

function getAllowedLocations() {
  const user = getUser();
  const locations = user && user.locations ? user.locations : [];
  return locations;
}

const locationDisplay = {
  srinagar: "Srinagar",
  // baramulla: "Baramulla",
  jammu: "Jammu",
};

// Map frontend sport names to backend sport names
const sportMapping = {
  Football: "football",
  "Box Cricket": "box-cricket",
  "Cricket Net": "cricket-net",
  "Pickle Ball": "pickle-ball",
};

const fieldMappingAgainstSport = {
  "pickle-ball": "Court",
  football: "Turf",
  "box-cricket": "Turf",
  "cricket-net": "Net",
};

// Location-wise facility mapping for each sport
const defaultFacilityMapping = {
  Football: [
    { turfKey: "turfA", displayName: "5 a side Turf A" },
    { turfKey: "turfB", displayName: "5 a side Turf B" },
    { turfKey: "turfC", displayName: "7 a side Turf C" },
  ],
  "Box Cricket": [
    { turfKey: "turfA", displayName: "5 a side Turf A" },
    { turfKey: "turfB", displayName: "5 a side Turf B" },
    { turfKey: "turfC", displayName: "7 a side Turf C" },
  ],
  "Cricket Net": [
    { turfKey: "turfA", displayName: "Net A" },
    { turfKey: "turfB", displayName: "Net B" },
  ],
  "Pickle Ball": [
    { turfKey: "turfA", displayName: "Court A" },
    { turfKey: "turfB", displayName: "Court B" },
  ],
};

const locationFacilityMapping = {
  srinagar: {
    Football: defaultFacilityMapping.Football,
    "Box Cricket": defaultFacilityMapping["Box Cricket"],
    "Cricket Net": defaultFacilityMapping["Cricket Net"],
    "Pickle Ball": defaultFacilityMapping["Pickle Ball"],
  },
  jammu: {
    "Pickle Ball": [
      { turfKey: "turfA", displayName: "Court 1" },
      { turfKey: "turfB", displayName: "Court 2" },
      { turfKey: "turfC", displayName: "Court 3" },
      { turfKey: "turfD", displayName: "Court 4" },
    ],
  },
};

const getFacilitiesForLocation = (location, sport) => {
  const locationKey = location?.toLowerCase();
  if (!locationKey) return defaultFacilityMapping[sport] || [];

  const locationFacilities =
    locationFacilityMapping[locationKey]?.[sport] || [];
  if (locationFacilities.length > 0) {
    return locationFacilities;
  }
  return defaultFacilityMapping[sport] || [];
};

const getDefaultTurfFor = (location, sport) => {
  const facilities = getFacilitiesForLocation(location, sport);
  return facilities[0]?.turfKey || "turfA";
};

const BlockSlot = () => {
  const dispatch = useDispatch();

  const allowedLocations = getAllowedLocations();
  const [selectedLocation, setSelectedLocation] = useState(
    allowedLocations[0] || "srinagar"
  );

  // Compute sports for current location
  const sportsList = useMemo(
    () => locationSportsMap[selectedLocation?.toLowerCase()] || [],
    [selectedLocation]
  );

  const [date, setDate] = useState(dayjs());
  const [allSlots, setAllSlots] = useState([]);
  const [toBeAvailableList, setToBeAvailableList] = useState([]);
  const [toBeUnavailableList, setToBeUnavailableList] = useState([]);

  const [selectedSport, setSelectedSport] = useState(() => {
    const initialLocation = allowedLocations[0] || "srinagar";
    const initialSports =
      locationSportsMap[initialLocation?.toLowerCase()] || [];
    return initialSports.length > 0 ? initialSports[0] : "";
  });

  const [selectedTurf, setSelectedTurf] = useState("turfA");
  const fetchingBookNowDetail = useSelector(isFetchBookNowPending);
  const blockUnblockPending = useSelector(isBlockUnblockPending);

  const {
    bookingAllowed,
    slotsAvailability,
    slots,
    availableSlots,
    pricingConfigs,
    dayMappings,
    datePresenceStatus,
    holidayBookingStatus,
  } = useSelector(getBookNowDataWithDateObject);

  const timeFormat = (timeString) => {
    const newTime = timeString.split(" - ");
    const timeString12hr = new Date(
      `1970-01-01T${newTime[0]}Z`
    ).toLocaleTimeString("en-US", {
      timeZone: "UTC",
      hour12: true,
      hour: "numeric",
      minute: "numeric",
    });
    const timeString12hr_ = new Date(
      `1970-01-01T${newTime[1]}Z`
    ).toLocaleTimeString("en-US", {
      timeZone: "UTC",
      hour12: true,
      hour: "numeric",
      minute: "numeric",
    });
    return `${timeString12hr} - ${timeString12hr_}`;
  };

  const dateChangeHandler = (value) => {
    dispatch(
      fetchBookNowDetail({
        date: value.format("DD/MM/YYYY"),
        sport: sportMapping[selectedSport],
        location: selectedLocation,
      })
    );
    setDate(value);
    setSelectedTurf(getDefaultTurfFor(selectedLocation, selectedSport));
    setToBeAvailableList([]);
    setToBeUnavailableList([]);
  };

  const turfChangeHandler = (value) => {
    setSelectedTurf(value);
    setToBeAvailableList([]);
    setToBeUnavailableList([]);
  };

  const locationChangeHandler = (e) => {
    const loc = e.target.value;
    setSelectedLocation(loc);
  };

  // When location changes, pick the first sport for that location
  useEffect(() => {
    const newList = locationSportsMap[selectedLocation?.toLowerCase()] || [];
    if (newList.length > 0) {
      setSelectedSport(newList[0]);
    } else {
      setSelectedSport("");
    }
  }, [selectedLocation]);

  useEffect(() => {
    if (!selectedSport) return;
    const facilities = getFacilitiesForLocation(selectedLocation, selectedSport);
    const facilityKeys = facilities.map((facility) => facility.turfKey);
    if (!facilityKeys.includes(selectedTurf)) {
      setSelectedTurf(facilities[0]?.turfKey || "turfA");
    }
  }, [selectedLocation, selectedSport, selectedTurf]);

  // Process booking data when Redux data is ready
  useEffect(() => {
    if (availableSlots && pricingConfigs && !fetchingBookNowDetail) {
      try {
        let dateObj = null;
        if (date.format) {
          const dateStr = date.format("DD/MM/YYYY");
          const [day, month, year] = dateStr.split("/");
          dateObj = new Date(year, month - 1, day);
        }

        const parsedData = parseBookNowApiData(
          availableSlots,
          pricingConfigs,
          dateObj,
          dayMappings,
          datePresenceStatus
        );

        if (
          holidayBookingStatus &&
          holidayBookingStatus.booking_allowed === false
        ) {
          parsedData.bookingAllowed = false;
        }

        dispatch(setBookNowDetail(parsedData));
      } catch (error) {
        console.error("Error parsing book now data:", error);
        const fallbackData = {
          date: date.format("DD/MM/YYYY"),
          bookingAllowed: holidayBookingStatus
            ? holidayBookingStatus.booking_allowed !== false
            : true,
          slotsAvailability: {
            turfA: Array(48).fill(true),
            turfB: Array(48).fill(true),
            turfC: Array(48).fill(true),
            turfD: Array(48).fill(true),
            turfE: Array(48).fill(true),
          },
          slots: {
            turfA: [],
            turfB: [],
            turfC: [],
            turfD: [],
            turfE: [],
          },
        };
        dispatch(setBookNowDetail(fallbackData));
      }
    }
  }, [
    availableSlots,
    pricingConfigs,
    dayMappings,
    datePresenceStatus,
    holidayBookingStatus,
    fetchingBookNowDetail,
    date,
    dispatch,
  ]);

  // Recompute visible slots for current turf with pricing
  useEffect(() => {
    const selectedTurfSlots = (slots && slots[selectedTurf]) || [];
    const filteredSlots = selectedTurfSlots.filter((slot) =>
      isTurfPricingAvailable(selectedTurf, slot.time)
    );
    setAllSlots(filteredSlots);
  }, [slots, selectedTurf]);

  // ✅ Fetch when sport/location/date are aligned (no timeout)
  useEffect(() => {
    if (!selectedSport) return;

    // Only fetch if the chosen sport is valid for the current location
    const validForLocation = (
      locationSportsMap[selectedLocation?.toLowerCase()] || []
    ).includes(selectedSport);
    if (!validForLocation) return;

    setSelectedTurf(getDefaultTurfFor(selectedLocation, selectedSport));
    setToBeAvailableList([]);
    setToBeUnavailableList([]);

    dispatch(
      fetchBookNowDetail({
        date: date.format("DD/MM/YYYY"),
        sport: sportMapping[selectedSport],
        location: selectedLocation,
      })
    );
  }, [selectedSport, selectedLocation, date, dispatch]);

  const getTimeSlot = (time) =>
    `${time} - ${moment(time, ["h:m a", "H:m"])
      .add(1, "hour")
      .format("HH:mm")}`;

  const isTurfPricingAvailable = (turfKey, timeSlot = null) => {
    const slotToCheck = timeSlot || "";
    if (!slotToCheck || !slots || !slots[turfKey]) {
      return false;
    }
    const turfSlots = slots[turfKey];
    return turfSlots.some((slot) => slot.time === slotToCheck);
  };

  const isSlotUnavailable = (time) => {
    const index = mapStartTimeToIndex[time];
    if (
      !slotsAvailability ||
      !slotsAvailability[selectedTurf] ||
      typeof index === "undefined"
    ) {
      return false;
    }
    return !slotsAvailability[selectedTurf][index];
  };

  const shouldDisableSlotTime = (time) => {
    const currentTime = getCurrentTimeString();
    const isToday = date.format("DD/MM/YYYY") === dayjs().format("DD/MM/YYYY");
    const isPastTime = currentTime > time;

    if ((isPastTime && isToday) || isSlotUnavailable(time)) {
      return true;
    }
    return false;
  };

  const slotClickHandler = (time, isSlotNotAvailable) => {
    if (isSlotNotAvailable) {
      setToBeAvailableList((prev) =>
        prev.includes(time) ? prev.filter((i) => i !== time) : [...prev, time]
      );
    } else {
      setToBeUnavailableList((prev) =>
        prev.includes(time) ? prev.filter((i) => i !== time) : [...prev, time]
      );
    }
  };

  const getSlotArr = () => {
    if (!slotsAvailability || !slotsAvailability[selectedTurf]) {
      return [];
    }

    const toBeAvailableIndexList = toBeAvailableList.map(
      (time) => mapStartTimeToIndex[time]
    );
    const toBeUnavailableIndexList = toBeUnavailableList.map(
      (time) => mapStartTimeToIndex[time]
    );
    return slotsAvailability[selectedTurf].map((flag, index) => {
      if (
        toBeAvailableIndexList.includes(index) ||
        toBeAvailableIndexList.includes(index - 1) ||
        toBeUnavailableIndexList.includes(index) ||
        toBeUnavailableIndexList.includes(index - 1)
      )
        return !flag;
      return flag;
    });
  };

  const submitHandler = async () => {
    try {
      const block_time = toBeUnavailableList;
      const unblock_time = toBeAvailableList;

      await dispatch(
        blockUnblockSlot({
          sport: sportMapping[selectedSport],
          turf_number: mapTurf[selectedTurf],
          date: date.format("DD/MM/YYYY"),
          location: selectedLocation,
          block_time,
          unblock_time,
          duration: 1,
        })
      );

      dispatch(
        fetchBookNowDetail({
          date: date.format("DD/MM/YYYY"),
          sport: sportMapping[selectedSport],
          location: selectedLocation,
        })
      );

      setToBeAvailableList([]);
      setToBeUnavailableList([]);
    } catch (error) {
      console.error("Error updating slots:", error);
    }
  };

  const renderSportButtons = () => (
    <div className={styles.sportsCards}>
      {sportsList.includes("Football") && (
        <button
          onClick={() => setSelectedSport("Football")}
          className={cx(styles.sportCard, {
            [styles.selectedSport]: selectedSport === "Football",
          })}
        >
          <div className={styles.footballSportCard} />
          <span>Football</span>
        </button>
      )}
      {sportsList.includes("Box Cricket") && (
        <button
          onClick={() => setSelectedSport("Box Cricket")}
          className={cx(styles.sportCard, {
            [styles.selectedSport]: selectedSport === "Box Cricket",
          })}
        >
          <div className={styles.cricketSportCard} />
          <span>Box Cricket</span>
        </button>
      )}
      {sportsList.includes("Cricket Net") && (
        <button
          onClick={() => setSelectedSport("Cricket Net")}
          className={cx(styles.sportCard, {
            [styles.selectedSport]: selectedSport === "Cricket Net",
          })}
        >
          <div className={styles.cricketSportCard} />
          <span>Cricket Nets</span>
        </button>
      )}
      {sportsList.includes("Pickle Ball") && (
        <button
          onClick={() => setSelectedSport("Pickle Ball")}
          className={cx(styles.sportCard, {
            [styles.selectedSport]: selectedSport === "Pickle Ball",
          })}
        >
          <div className={styles.pickeBallSportCard} />
          <span>Pickleball</span>
        </button>
      )}
    </div>
  );

  if (!selectedSport || !selectedLocation) {
    return <Loader size="large" />;
  }

  return (
    <>
      <div className={styles.container}>
        <div className={styles.calendarContainer}>
          <div className={styles.sportContainer}>
            <h3 className={styles.sectionHeader}>Location:</h3>
            <select
              value={selectedLocation}
              onChange={locationChangeHandler}
              className={styles.locationSelect}
            >
              {allowedLocations.map((loc) => (
                <option key={loc} value={loc}>
                  {locationDisplay[loc] || loc}
                </option>
              ))}
            </select>
            <h3 className={styles.sectionHeader}>Select Sports:</h3>
            {renderSportButtons()}
          </div>
          <h3 className={styles.sectionHeader}>Select Date:</h3>
          <Calendar value={date} setValue={dateChangeHandler} />
          <div className={styles.date}>{date.format("DD MMM, YYYY")}</div>
        </div>

        <div className={styles.turfContainer}>
          {fetchingBookNowDetail ? (
            <>
              <Loader size="large" />
            </>
          ) : bookingAllowed ? (
            <>
              <div className={styles.slotContainer}>
                <h3 className={styles.sectionHeader}>
                  Select {fieldMappingAgainstSport[sportMapping[selectedSport]]}
                  :
                </h3>
                <div className={styles.availableTurf}>
                  {getFacilitiesForLocation(
                    selectedLocation,
                    selectedSport
                  ).map((facility) => (
                    <button
                      key={facility.turfKey}
                      onClick={() => turfChangeHandler(facility.turfKey)}
                      className={cx(styles.turfCard, {
                        [styles.selectedTurf]:
                          selectedTurf === facility.turfKey,
                      })}
                    >
                      {facility.displayName}
                    </button>
                  ))}
                </div>
                <h3 className={styles.sectionHeader}>Select Slot:</h3>
                <div className={styles.slots}>
                  {(allSlots || []).map(({ selected = false, time, cost }) => (
                    <button
                      key={time}
                      onClick={() =>
                        slotClickHandler(time, isSlotUnavailable(time))
                      }
                      className={cx(styles.slot, styles.available, {
                        [styles.selectedTime]: selected,
                        [styles.unavailable]: isSlotUnavailable(time),
                        [styles.madeUnavailable]:
                          toBeUnavailableList.includes(time),
                        [styles.madeAvailable]:
                          toBeAvailableList.includes(time),
                      })}
                    >
                      {timeFormat(getTimeSlot(time))}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <span className={styles.msg}>
              Bookings are not allowed for the selected date.
            </span>
          )}
          <div className={styles.btnContainer}>
            <button
              className={styles.btn}
              onClick={submitHandler}
              disabled={
                isEmpty(toBeAvailableList) && isEmpty(toBeUnavailableList)
              }
            >
              {blockUnblockPending ? <LoaderForButton /> : "PROCEED"}
            </button>
          </div>
        </div>
      </div>
      <div className="footer"></div>
    </>
  );
};

export default BlockSlot;
